"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const locations = [{
  id: 1,
  name: "NORTH SECTOR HUB",
  address: "COORD: 40.7128° N, 74.0060° W",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/image_m61rvlsi.png",
  link: "#"
}, {
  id: 2,
  name: "WESTERN DIVISION",
  address: "COORD: 34.0522° N, 118.2437° W",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/location_riverside_estate.png",
  link: "#"
}, {
  id: 3,
  name: "CENTRAL ANALYSIS",
  address: "COORD: 39.7392° N, 104.9903° W",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/location_highland_retreat.png",
  link: "#"
}];
export default function OurLocations() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section data-section-id="85225" id="locations-overview" className="bg-background text-foreground py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-5xl font-extrabold tracking-tighter text-foreground mb-6 font-sans">
            Strategic Operations
          </h2>
          <Separator className="mb-12 bg-border h-px w-full" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {locations.map((loc, i) => <LocationCard key={loc.id} location={loc} index={i} />)}
        </div>
      </div>
    </section>;
}
function LocationCard({
  location,
  index
}: {
  location: (typeof locations)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`group transition-all duration-700 ease-out h-full hover:scale-[1.02] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full overflow-hidden border-border shadow-sm hover:shadow-xl transition-all duration-300 bg-card flex flex-col rounded-lg">
        <div className="relative h-64 w-full shrink-0">
          <img src={location.image} alt={location.name} className="object-cover w-full h-full grayscale hover:grayscale-0 transition-all duration-500" />
          <div className="absolute inset-0 bg-primary/5 transition-opacity duration-300" />
        </div>
        <CardContent className="px-6 pb-6 pt-6 flex-grow flex flex-col items-start text-left">
          <h3 className="text-2xl font-extrabold text-foreground mb-3 tracking-tighter font-sans relative after:absolute after:bottom-0 after:left-1/2 after:w-0 after:h-0.5 after:bg-primary after:transition-all group-hover:after:w-full group-hover:after:left-0 pb-1">
            {location.name}
          </h3>
          <p className="text-muted-foreground mb-8 flex-grow text-sm font-mono tracking-tight">
            {location.address}
          </p>
          <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg h-12 text-base font-bold shadow-none transition-colors">
            <Link to={location.link}>
              <MapPin className="mr-2 h-5 w-5" />
              DEPLOY UNIT
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>;
}